import { useTranslation } from 'react-i18next';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import {
  connectionGridSchema,
  convertDefaultValue,
  defaultValueForConnectionForm
} from './utils';
import { ControlledTextField, Drawer } from 'components/shared/index';
import { LoadingButton } from '@mui/lab';
import ControlledMultiSelect from 'components/shared/ControlledFields/ControlledMultiSelect';
import { useSaveConnections } from './useEditConfigs';
import { useGetEmployers } from '../../../../queries';
import { useGetImportEmployeeConfigEmployer } from './useGetConnectionTableData';
import { isNil, omit } from 'ramda';
import { toOptions } from 'utils/toOptions';
import Skeleton from '@mui/material/Skeleton';

const ConnectionDetailDrawer = ({ containerOpen, handleClose, tableRow }) => {
  const { t } = useTranslation('common');
  const { data: employers, isFetching: isFetchingConfig } =
    useGetImportEmployeeConfigEmployer();
  const { data: empArray, isFetching } = useGetEmployers();

  return (
    <Drawer
      open={containerOpen}
      onClose={handleClose}
      title={t('employeers')}
      anchor="right"
      PaperProps={{
        sx: {
          width: {
            xs: '100%',
            sm: '70%',
            md: '50%',
            lg: '35%',
            xl: '30%'
          }
        }
      }}
    >
      {isFetching && isFetchingConfig ? (
        <Skeleton />
      ) : (
        <ConnectionForm
          rowData={tableRow}
          employers={employers}
          empArray={empArray}
        />
      )}
    </Drawer>
  );
};

const ConnectionForm = ({ rowData, employers, empArray }) => {
  const { t } = useTranslation('common');
  const { saveMain, saveEmployees, isLoading } = useSaveConnections(rowData);

  const methods = useForm({
    defaultValues: !isNil(rowData)
      ? connectionGridSchema.cast({
          ...rowData,
          employerId: convertDefaultValue(empArray, employers).map((item) => {
            return item.employerId;
          })
        })
      : connectionGridSchema.cast({
          ...defaultValueForConnectionForm
        }),
    resolver: yupResolver(connectionGridSchema)
  });

  const onSubmit = async (data) => {
    await saveMain(omit(['employerId'], data));
    await saveEmployees(data);
  };

  return (
    <MultiStepForm onSubmit={onSubmit} methods={methods}>
      <MultiStepForm.Section title={t('importEmployeeSettings')}>
        <ControlledTextField
          control={methods.control}
          name="accountFormat"
          label={t('accountName')}
        />
        <ControlledTextField
          control={methods.control}
          name="host"
          label={t('host')}
        />
        <ControlledTextField
          control={methods.control}
          name="apiPrefix"
          label={t('apiPrefix')}
        />
        <ControlledTextField
          control={methods.control}
          name="user"
          label={t('username')}
        />
        <ControlledTextField
          type="password"
          control={methods.control}
          name="apikey"
          label={t('apiKey')}
        />
      </MultiStepForm.Section>
      <MultiStepForm.Section title={t('addNewEmployee')}>
        <ControlledMultiSelect
          fullWidth
          showList={true}
          label={t('employeeName')}
          variant="outlined"
          name="employerId"
          options={toOptions(empArray ?? [], 'employerId', 'name')}
        />
      </MultiStepForm.Section>
      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          color="dark"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default ConnectionDetailDrawer;
